import type {
  ChartFeedConnectionStatus,
  ChartFeedErrorMessage,
  ChartFeedHistoryResponse,
  ChartFeedPriceMessage,
  ChartFeedProvider,
  ChartFeedSymbolsResponse,
} from './types'

interface FetchChartFeedHistoryArgs {
  basicToken: string
  provider: ChartFeedProvider
  symbol: string
  interval: string
  outputsize?: number
  startDate?: string
  endDate?: string
  order?: 'asc' | 'desc'
  signal?: AbortSignal
}

interface OpenChartFeedStreamArgs {
  baseHttpUrl?: string
  basicToken: string
  provider: ChartFeedProvider
  symbol: string
  onStatusChange?: (status: ChartFeedConnectionStatus) => void
  onPriceMessage: (message: ChartFeedPriceMessage) => void
  onErrorMessage?: (message: string) => void
}

export interface ChartFeedStreamConnection {
  close: () => void
}

function parseSymbolsResponse(payload: unknown): ChartFeedSymbolsResponse {
  const fromArray = (value: unknown): string[] => {
    if (!Array.isArray(value)) {
      return []
    }

    const symbols = value
      .map((entry) => {
        if (typeof entry === 'string') {
          return entry
        }

        if (entry && typeof entry === 'object') {
          const candidate =
            (entry as { logicalSymbol?: unknown }).logicalSymbol ??
            (entry as { symbol?: unknown }).symbol ??
            (entry as { name?: unknown }).name
          if (typeof candidate === 'string') {
            return candidate
          }
        }

        return ''
      })
      .map((symbol) => symbol.trim())
      .filter(Boolean)

    return Array.from(new Set(symbols))
  }

  const direct = fromArray(payload)
  if (direct.length > 0) {
    return { symbols: direct }
  }

  if (payload && typeof payload === 'object') {
    const fromObject = fromArray((payload as { symbols?: unknown }).symbols)
    if (fromObject.length > 0) {
      return { symbols: fromObject }
    }

    const fromItems = fromArray((payload as { items?: unknown }).items)
    if (fromItems.length > 0) {
      return { symbols: fromItems }
    }
  }

  return { symbols: [] }
}

function toFiniteNumber(value: unknown): number | undefined {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }

  return undefined
}

function parsePriceMessage(payload: unknown): ChartFeedPriceMessage | null {
  if (!payload || typeof payload !== 'object') {
    return null
  }

  const candidate = payload as Partial<ChartFeedPriceMessage>
  const price = toFiniteNumber(candidate.price)
  if (
    candidate.type !== 'price' ||
    typeof candidate.logicalSymbol !== 'string' ||
    typeof candidate.providerSymbol !== 'string' ||
    typeof candidate.basis !== 'string' ||
    typeof candidate.provider !== 'string' ||
    price === undefined ||
    typeof candidate.receivedAt !== 'string'
  ) {
    return null
  }

  return {
    type: 'price',
    provider: candidate.provider,
    logicalSymbol: candidate.logicalSymbol,
    providerSymbol: candidate.providerSymbol,
    basis: candidate.basis,
    price,
    bid: toFiniteNumber(candidate.bid),
    ask: toFiniteNumber(candidate.ask),
    receivedAt: candidate.receivedAt,
  }
}

function isErrorMessage(payload: unknown): payload is ChartFeedErrorMessage {
  if (!payload || typeof payload !== 'object') {
    return false
  }

  const candidate = payload as Partial<ChartFeedErrorMessage>
  return candidate.type === 'error' && typeof candidate.error === 'string'
}

function resolveSourceFromQueryOrEnv(
  queryParam: string,
  envValue: string | undefined,
  fallbackEnvValue?: string,
): string | null {
  const params = new URLSearchParams(window.location.search)
  const fromQuery = params.get(queryParam)
  if (typeof fromQuery === 'string' && fromQuery.trim()) {
    return fromQuery.trim()
  }

  if (typeof envValue === 'string' && envValue.trim()) {
    return envValue.trim()
  }

  if (typeof fallbackEnvValue === 'string' && fallbackEnvValue.trim()) {
    return fallbackEnvValue.trim()
  }

  return null
}

function normalizeHttpSource(source: string): string {
  if (/^https?:\/\//i.test(source)) {
    return source
  }

  if (/^wss?:\/\//i.test(source)) {
    return source.replace(/^ws/i, 'http')
  }

  if (/^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\])(?::\d+)?(?:\/|$)/i.test(source)) {
    return `http://${source}`
  }

  return `https://${source}`
}

export function resolveChartFeedHttpBaseUrl(): string {
  const source =
    resolveSourceFromQueryOrEnv(
      'chartFeedUrl',
      process.env.REACT_APP_CHART_FEED_URL,
      process.env.REACT_APP_TOURNAMENT_SERVER_DOMAIN,
    ) ?? window.location.origin
  return new URL(normalizeHttpSource(source), window.location.origin).toString()
}

export function resolvePocketBaseHttpBaseUrl(): string {
  const source =
    resolveSourceFromQueryOrEnv(
      'pocketBaseUrl',
      process.env.REACT_APP_POCKET_BASE_URL,
      process.env.REACT_APP_POCKET_BASE_SERVER_DOMAIN,
    ) ?? window.location.origin
  return new URL(normalizeHttpSource(source), window.location.origin).toString()
}

export function resolveChartFeedWsBaseUrl(baseHttpUrl?: string): string {
  const httpUrl = new URL(baseHttpUrl ?? resolveChartFeedHttpBaseUrl())
  const protocol = httpUrl.protocol === 'https:' ? 'wss:' : 'ws:'
  return `${protocol}//${httpUrl.host}`
}

export async function fetchChartFeedSymbols(signal?: AbortSignal): Promise<string[]> {
  const endpoint = new URL('/api/collections/pbc_1322267247/records', resolvePocketBaseHttpBaseUrl())
  endpoint.searchParams.set('page', '1')
  endpoint.searchParams.set('perPage', '200')
  endpoint.searchParams.set('sort', 'symbol')
  endpoint.searchParams.set('skipTotal', '1')
  endpoint.searchParams.set('fields', 'symbol')

  let response: Response
  try {
    response = await fetch(endpoint.toString(), {
      method: 'GET',
      signal,
      headers: {
        Accept: 'application/json',
      },
    })
  } catch {
    throw new Error('Unable to reach PocketBase symbol collection.')
  }

  if (!response.ok) {
    throw new Error(`Symbol discovery failed (${response.status}).`)
  }

  let payload: unknown
  try {
    payload = await response.json()
  } catch {
    throw new Error('Symbol discovery returned invalid JSON.')
  }

  const parsed = parseSymbolsResponse(payload)
  return parsed.symbols.filter((symbol) => !symbol.startsWith('F:'))
}

export function openChartFeedStream(args: OpenChartFeedStreamArgs): ChartFeedStreamConnection {
  const { basicToken, provider, symbol, onPriceMessage, onErrorMessage, onStatusChange, baseHttpUrl } = args
  const wsUrl = new URL('/ws/chart-feed-test/stream', resolveChartFeedWsBaseUrl(baseHttpUrl))
  wsUrl.searchParams.set('basic', basicToken.trim())

  onStatusChange?.('connecting')

  const socket = new WebSocket(wsUrl.toString())

  socket.addEventListener('open', () => {
    socket.send(
      JSON.stringify({
        action: 'subscribe',
        provider,
        symbol,
      }),
    )
    onStatusChange?.('subscribed')
  })

  socket.addEventListener('message', (event) => {
    let payload: unknown
    try {
      payload = JSON.parse(String(event.data)) as unknown
    } catch {
      return
    }

    const priceMessage = parsePriceMessage(payload)
    if (priceMessage) {
      onPriceMessage(priceMessage)
      onStatusChange?.('streaming')
      return
    }

    if (isErrorMessage(payload)) {
      onStatusChange?.('error')
      onErrorMessage?.(payload.error)
    }
  })

  socket.addEventListener('error', () => {
    onStatusChange?.('error')
    onErrorMessage?.('Chart feed websocket connection error.')
  })

  socket.addEventListener('close', () => {
    onStatusChange?.('closed')
  })

  return {
    close: () => {
      if (socket.readyState === WebSocket.CLOSING || socket.readyState === WebSocket.CLOSED) {
        return
      }
      socket.close()
    },
  }
}

export async function fetchChartFeedHistory(args: FetchChartFeedHistoryArgs): Promise<ChartFeedHistoryResponse> {
  const endpoint = new URL('/chart-feed-test/history', resolveChartFeedHttpBaseUrl())
  endpoint.searchParams.set('basic', args.basicToken.trim())
  endpoint.searchParams.set('provider', args.provider)
  endpoint.searchParams.set('symbol', args.symbol)
  endpoint.searchParams.set('interval', args.interval)
  endpoint.searchParams.set('outputsize', String(args.outputsize ?? 300))

  if (args.startDate) {
    endpoint.searchParams.set('start_date', args.startDate)
  }
  if (args.endDate) {
    endpoint.searchParams.set('end_date', args.endDate)
  }
  if (args.order) {
    endpoint.searchParams.set('order', args.order)
  }

  let response: Response
  try {
    response = await fetch(endpoint.toString(), {
      method: 'GET',
      signal: args.signal,
      headers: {
        Accept: 'application/json',
      },
    })
  } catch {
    throw new Error('Unable to reach chart-feed-test history endpoint.')
  }

  let payload: unknown
  try {
    payload = await response.json()
  } catch {
    throw new Error('History endpoint returned invalid JSON.')
  }

  if (!response.ok) {
    if (payload && typeof payload === 'object' && typeof (payload as { error?: unknown }).error === 'string') {
      throw new Error((payload as { error: string }).error)
    }
    throw new Error(`History request failed (${response.status}).`)
  }

  if (!payload || typeof payload !== 'object') {
    throw new Error('History response payload is invalid.')
  }

  const data = (payload as { data?: unknown }).data
  if (!Array.isArray(data)) {
    throw new Error('History response is missing data array.')
  }

  return payload as ChartFeedHistoryResponse
}
